<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { debounce } from "lodash-es";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";
import {
  getDate,
  getHour,
  getDifference,
  getDiffBreaks,
} from "@/common/utils/dates";

import { sampleGet, samplePatch } from "@/common/api/rest/sample";
import {
  evaluationGet,
  evaluationSampleList,
} from "@/common/api/rest/evaluation";
import { analysisGet } from "@/common/api/rest/analysis";
import { resultList } from "@/common/api/rest/result";
import {
  fileAvatarCreate,
  fileAvatarUpdate,
  fileAvatarDelete,
} from "@/common/api/rest/file/avatar";

import type { SampleType } from "@/common/types/sample";
import type { EvaluationType } from "@/common/types/evaluation";
import type { AnalysisType } from "@/common/types/analysis";
import type { ResultType } from "@/common/types/result";

import { RegisterTheme } from "@/common/themes";
import { sampleForm } from "@/components";
import {
  baseConfirm,
  baseAvatar,
  iconMenu,
  svgIcon,
} from "@/common/components";

const { t, tm } = useI18n();
const router = useRouter();
const route = useRoute();

const sample = ref<SampleType | null>(null);
const evaluation = ref<EvaluationType | null>(null);
const evaluationSample = ref<SampleType[] | null>(null);
const analyse = ref<AnalysisType | null>(null);
const results = ref<ResultType[] | null>(null);
const result = ref<ResultType | null>(null);

const selectedMenu = ref<string>("home");

const showDelAvatar = ref(false);

const accountId = computed(() => String(route.params.id));
const sampleId = computed(() => String(route.params.sample));

const analyseStartDate = computed(() => {
  if (!analyse.value) {
    return null;
  }

  return analyse.value.start_edited ?? analyse.value.start_date;
});

const analyseEndDate = computed(() => {
  if (!analyse.value) {
    return null;
  }

  return analyse.value.end_edited ?? analyse.value.end_date;
});

const analysisStatusEnum = computed(
  () => tm("enums.analysis_status") as Record<string, string>,
);

const menuTabs = computed(() => [
  {
    key: "home",
    icon: "home-line",
    label: t("common.labels.sample"),
  },
  {
    key: "memory",
    icon: "no-image",
    label: "Memória de Campo",
  },
  {
    key: "laboratory",
    icon: "laboratory",
    label: "Laboratório e Resultado",
  },
]);

function back() {
  router.push(`/${accountId.value}/register/sample`);
}

async function fetchSample() {
  if (!sampleId.value) {
    return;
  }

  await sampleGet(accountId.value, sampleId.value)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        sample.value = data;
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function fetchEvaluation() {
  if (!sample.value?.evaluation_id) {
    return;
  }

  evaluationGet(sample.value.evaluation_id)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        evaluation.value = data;
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function fetchEvaluationSample() {
  if (!sample.value?.evaluation_id) {
    return;
  }

  evaluationSampleList(sample.value.evaluation_id)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        evaluationSample.value = data.list;
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function fetchAnalysis() {
  if (!sample.value?.analysis_id) {
    return;
  }

  analysisGet(sample.value.analysis_id)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        analyse.value = data;
        console.log("analyse", analyse.value);
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function fetchResult() {
  if (!sample.value?.analysis_id) {
    return;
  }

  resultList(sample.value.analysis_id)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        results.value = data.list;

        const resultFind = data.list?.find(
          (item: any) => item.sample_id === sample.value?.sample_id,
        );

        if (resultFind) {
          result.value = resultFind;
        }
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function intialize() {
  await fetchSample();
  fetchEvaluation();
  fetchEvaluationSample();
  fetchAnalysis();
  fetchResult();
}

function patchSample(payload: SampleType) {
  if (!sampleId.value) {
    return;
  }

  samplePatch(accountId.value, sampleId.value, payload)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        fetchSample();
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addAvatar(avatar: string) {
  fileAvatarCreate(accountId.value, {
    owner_id: sampleId.value,
    type: "SAMPLE",
    file: avatar,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchSample();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function updateAvatar(avatar: string) {
  if (!sample.value?.avatar) return;

  fileAvatarUpdate(accountId.value, sample.value.avatar.avatar_id, {
    file: avatar,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchSample();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changeAvatar(avatar: string) {
  if (sample.value?.avatar) {
    updateAvatar(avatar);
  } else {
    addAvatar(avatar);
  }
}

function delAvatar() {
  showDelAvatar.value = true;
}

function delAvatarConfirm() {
  if (!sample.value?.avatar) return;

  fileAvatarDelete(accountId.value, sample.value.avatar.avatar_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchSample();
        showDelAvatar.value = false;
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changeMenu(key: string) {
  selectedMenu.value = key;
  intialize();
}

function dateDiff(start_date: string | null, end_date: string | null) {
  if (!start_date) {
    return "0m 0s";
  }

  const diff = getDifference(
    start_date,
    end_date ? end_date : new Date().toISOString(),
  );

  return diff.text;
}

const debounceForm = debounce(patchSample, 1000);

onMounted(() => {
  intialize();
});
</script>

<template>
  <register-theme tab-active="sample">
    <template #title>
      <div class="flex flex-row justify-between items-center w-full">
        <div class="flex flex-row gap-1 items-center">
          <svg-icon icon="samples" size="2rem" />
          {{ t("common.labels.edit", { concat: t("common.labels.sample") }) }}
        </div>

        <button type="button" class="btn btn-back btn-sm" @click="back()">
          <svg-icon icon="close" size="1.25rem" />
        </button>
      </div>
    </template>

    <template #content>
      <icon-menu :tabs="menuTabs" :active="selectedMenu" @selected="changeMenu">
        <template #content>
          <div class="forms">
            <template v-if="selectedMenu === 'home'">
              <div class="title">
                {{ t("common.labels.sample") }}
              </div>

              <base-avatar
                :url="sample?.avatar?.large"
                :is-edit="true"
                @change="changeAvatar"
                @del="delAvatar()"
              />

              <sample-form
                width="100%"
                :data="sample"
                :is-patch="true"
                :show-status="true"
                :in-use="!!sample?.analysis_id"
                @update-data="debounceForm"
              />
            </template>

            <template v-if="selectedMenu === 'memory'">
              <div class="title">Memória de Campo</div>

              <!-- DADOS DA ANÁLISE -->
              <div class="field">
                <div class="field-title">DADOS DA ANÁLISE</div>
                <div class="field-content">
                  <template v-if="analyse">
                    <div class="field-line">
                      <span class="field-label">Avaliação</span>
                      <span class="field-value">{{ evaluation?.code }}</span>

                      <span class="field-label">Análise</span>
                      <span class="field-value">{{ analyse?.code }}</span>

                      <span class="field-label">Status</span>
                      <span class="field-value uppercase">
                        {{ analysisStatusEnum[analyse?.status] }}
                      </span>

                      <span class="field-label"> Início </span>
                      <span class="field-value">
                        {{ getDate(analyseStartDate) }}
                        -
                        {{ getHour(analyseStartDate) }}
                        <template v-if="analyse.start_edited">
                          <span style="color: darkred"> (editado) </span>
                        </template>
                      </span>

                      <span class="field-label">Fim</span>
                      <span class="field-value">
                        {{ getDate(analyseEndDate) }}
                        -
                        {{ getHour(analyseEndDate) }}
                        <template v-if="analyse.end_edited">
                          <span style="color: darkred"> (editado) </span>
                        </template>
                      </span>

                      <span class="field-label">Tempo</span>
                      <span class="field-value">
                        {{ dateDiff(analyseStartDate, analyseEndDate) }}
                      </span>
                    </div>
                  </template>
                  <template v-else>
                    <div class="field-empty">
                      Essa amostra ainda não foi associada a uma análise.
                    </div>
                  </template>
                </div>
              </div>

              <!-- DADOS DO RESULTADO -->
              <div class="field">
                <div class="field-title">DADOS DO RESULTADO</div>
                <div class="field-content">
                  <template v-if="result">
                    <div class="field-line">
                      <span class="field-label">Nome</span>
                      <span class="field-value">{{ result.name }}</span>

                      <span class="field-label">Resultado</span>
                      <span class="field-value">{{ result.result }}</span>

                      <span class="field-label">Observação</span>
                      <span class="field-value">{{ result.description }}</span>
                    </div>
                  </template>
                  <template v-else>
                    <div class="field-empty">
                      Essa amostra ainda não possui resultado registrado.
                    </div>
                  </template>
                </div>
              </div>
            </template>
          </div>
        </template>
      </icon-menu>

      <base-confirm
        :show="showDelAvatar"
        :title="
          t('common.labels.remove', { concat: t('common.labels.avatar') })
        "
        :message="
          t('common.texts.remove_message', {
            concat: t('common.labels.avatar'),
          })
        "
        @cancel="showDelAvatar = false"
        @confirm="delAvatarConfirm()"
      />
    </template>
  </register-theme>
</template>

<style lang="scss" scoped>
.forms {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;

  .title {
    display: flex;
    flex-direction: column;
    padding-inline: 0.5rem;
    border-bottom: 1px solid var(--border);
    margin-block: 2rem;
    padding-block-end: 0.5rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    border: 1px solid var(--border);
    padding-block: 0.5rem;
    padding-inline: 0.75rem;

    .field-title {
      font-weight: bold;
      border-bottom: 1px solid var(--border);
      padding-bottom: 0.25rem;
    }

    .field-content {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      .field-line {
        display: grid;
        grid-template-columns: max-content 1fr;
        gap: 1rem;
        row-gap: 0.5rem;
        font-size: 0.875rem;

        .field-label {
          text-transform: uppercase;
        }

        .field-value {
          flex: 2;
          font-weight: bold;
        }
      }

      .field-empty {
        font-style: italic;
        color: var(--text-secondary);
      }
    }
  }
}
</style>
